import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import './SupportChatGeneric.css';

const SupportChatGeneric = () => {
    const [searchParams] = useSearchParams();
    const urlConversationId = searchParams.get('conversation_id');

    const [user, setUser] = useState(null);
    const [openConversations, setOpenConversations] = useState([]);
    const [closedConversations, setClosedConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [expandedConversations, setExpandedConversations] = useState({});
    const [replyTexts, setReplyTexts] = useState({});
    const [sendingReply, setSendingReply] = useState({});
    const [closingConversation, setClosingConversation] = useState({});
    const [confirmCloseId, setConfirmCloseId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const messagesEndRefs = useRef({});
    const targetConvRef = useRef(null);
    const loadedRef = useRef(false);

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        loadInitialData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const userData = await apiService.getUserInfo();
            const userId = userData.user?.user_id;
            const [openResult, closedResult] = await Promise.all([
                apiService.getSupportConversations('open', userId),
                apiService.getSupportConversations('closed', userId)
            ]);
            setUser(userData.user);
            const openConvs = openResult.success ? (openResult.data?.conversations || []) : [];
            const closedConvs = closedResult.success ? (closedResult.data?.conversations || []) : [];
            setOpenConversations(openConvs);
            setClosedConversations(closedConvs);

            // Se conversation_id foi passado na URL, auto-expandir essa conversa
            if (urlConversationId) {
                const allConvs = [...openConvs, ...closedConvs];
                const found = allConvs.find(c => String(c.conversation_id) === String(urlConversationId));
                if (found) {
                    setExpandedConversations({ [found.conversation_id]: true });
                }
            }
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError('Erro ao carregar dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const reloadConversation = async (conversationId) => {
        const userId = user?.user_id;
        const [openResult, closedResult] = await Promise.all([
            apiService.getSupportConversations('open', userId),
            apiService.getSupportConversations('closed', userId)
        ]);
        if (openResult.success) {
            setOpenConversations(openResult.data?.conversations || []);
        }
        if (closedResult.success) {
            setClosedConversations(closedResult.data?.conversations || []);
        }
    };

    const handleRefreshAll = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await reloadConversation();
        } catch (err) {
            console.error('Erro ao atualizar conversas:', err);
        } finally {
            setRefreshing(false);
        }
    };

    // Scroll para a conversa alvo quando aberta via URL
    useEffect(() => {
        if (!loading && urlConversationId && targetConvRef.current) {
            setTimeout(() => {
                targetConvRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
    }, [loading, urlConversationId]);

    const toggleConversation = (conversationId) => {
        setExpandedConversations(prev => ({ ...prev, [conversationId]: !prev[conversationId] }));
    };

    const handleReplyChange = (conversationId, text) => {
        setReplyTexts(prev => ({ ...prev, [conversationId]: text }));
    };

    const handleSendReply = async (conversation) => {
        const conversationId = conversation.conversation_id;
        const message = (replyTexts[conversationId] || '').trim();
        if (!message || !user) return;

        const firstMessage = conversation.messages?.[0];
        if (!firstMessage) return;

        const supportAgent = 'Suporte (' + (user.fullname || 'Admin') + ')';

        setSendingReply(prev => ({ ...prev, [conversationId]: true }));
        try {
            const result = await apiService.replySupportConversation({
                conversation_id: conversationId,
                device_code: firstMessage.device_code,
                support_agent: supportAgent,
                message
            });
            if (result.success) {
                setReplyTexts(prev => ({ ...prev, [conversationId]: '' }));
                await reloadConversation(conversationId);
            }
        } catch (err) {
            console.error('Erro ao enviar mensagem:', err);
        } finally {
            setSendingReply(prev => ({ ...prev, [conversationId]: false }));
        }
    };

    const handleKeyDown = (e, conversation) => {
    };

    const handleCloseConversation = (conversationId) => {
        setConfirmCloseId(conversationId);
    };

    const confirmClose = async () => {
        const conversationId = confirmCloseId;
        setConfirmCloseId(null);
        if (!conversationId) return;

        setClosingConversation(prev => ({ ...prev, [conversationId]: true }));
        try {
            const result = await apiService.closeSupportConversation(conversationId);
            if (result.success) {
                await reloadConversation(conversationId);
            }
        } catch (err) {
            console.error('Erro ao encerrar conversa:', err);
        } finally {
            setClosingConversation(prev => ({ ...prev, [conversationId]: false }));
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderConversationCard = (conv) => {
        const isOpen = expandedConversations[conv.conversation_id];
        const replyText = replyTexts[conv.conversation_id] || '';
        const isSending = sendingReply[conv.conversation_id];
        const isClosing = closingConversation[conv.conversation_id];
        const isConversationOpen = conv.messages?.[0]?.conversation_status !== 'closed';
        const messages = (conv.messages || []).sort((a, b) => new Date(a.created) - new Date(b.created));
        const lastMessage = messages[messages.length - 1];
        const isPending = lastMessage && lastMessage.sender_type === 'visitor';
        const deviceModel = conv.device_model || messages.find(m => m.device_model)?.device_model;
        const appVersion = conv.app_version || messages.find(m => m.app_version)?.app_version;

        const isTarget = urlConversationId && String(conv.conversation_id) === String(urlConversationId);

        return (
            <div
                key={conv.conversation_id}
                className={`scg-conversation ${isTarget ? 'scg-conversation-highlight' : ''}`}
                ref={isTarget ? targetConvRef : null}
            >
                <button
                    className="scg-conversation-header"
                    onClick={() => toggleConversation(conv.conversation_id)}
                >
                    <div className="scg-conversation-info">
                        <div className="scg-conversation-main">
                            <span className="scg-visitor-name">👤 {conv.visitor_name || 'Visitante'}</span>
                            <span className="scg-visitor-company">🏢 {conv.visitor_company || '—'}</span>
                            <span className="scg-visitor-store">🏪 {conv.visitor_store || '—'}</span>
                            {deviceModel && <span className="scg-device-model">📱 {deviceModel}</span>}
                            {appVersion && <span className="scg-app-version">v{appVersion}</span>}
                            <span className="scg-msg-count">{messages.length} msg</span>
                            {isPending && isConversationOpen && <span className="scg-tag-pending">PENDENTE</span>}
                        </div>
                        {(conv.last_signed_user_fullname || conv.last_signed_user_username || conv.last_signed_subdomain) && (
                            <div className="scg-conversation-meta">
                                <span className="scg-meta-label">Último usuário do celular autenticado:</span>
                                {(conv.last_signed_user_fullname || conv.last_signed_user_username) && (
                                    <span className="scg-meta-user">👤 {conv.last_signed_user_fullname || conv.last_signed_user_username}</span>
                                )}
                                {conv.last_signed_user_username && (
                                    <span className="scg-meta-username">@{conv.last_signed_user_username}</span>
                                )}
                                {conv.last_signed_subdomain && (
                                    <span className="scg-meta-subdomain">🌐 {conv.last_signed_subdomain}</span>
                                )}
                            </div>
                        )}
                        {lastMessage && (
                            <div className="scg-conversation-preview">
                                {lastMessage.message?.substring(0, 80)}{lastMessage.message?.length > 80 ? '...' : ''}
                                <span className="scg-preview-time">{formatTime(lastMessage.created)}</span>
                            </div>
                        )}
                    </div>
                    <span className={`scg-toggle-icon ${isOpen ? 'open' : ''}`}>▶</span>
                </button>

                {isOpen && (
                    <>
                        <div className="scg-messages">
                            {messages.map(msg => {
                                const isAgent = msg.sender_type === 'support' || msg.support_agent;
                                return (
                                    <div
                                        key={msg.id}
                                        className={`scg-message ${isAgent ? 'from-agent' : 'from-visitor'}`}
                                    >
                                        <div className="scg-message-header">
                                            <span className="scg-message-sender">
                                                {isAgent
                                                    ? (msg.support_agent || 'Suporte')
                                                    : (msg.visitor_name || 'Visitante')}
                                            </span>
                                            <span className="scg-message-time">
                                                {formatTime(msg.created)}
                                            </span>
                                        </div>
                                        <div className="scg-message-text">{msg.message}</div>
                                    </div>
                                );
                            })}
                            <div ref={el => messagesEndRefs.current[conv.conversation_id] = el} />
                        </div>

                        {conv.messages?.[0]?.conversation_status !== 'closed' && (
                            <div className="scg-reply-form">
                                <textarea
                                    rows="3"
                                    className="scg-reply-input"
                                    placeholder="Digite sua resposta..."
                                    value={replyText}
                                    onChange={(e) => handleReplyChange(conv.conversation_id, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, conv)}
                                    disabled={isSending}
                                />
                                <button
                                    className="scg-reply-btn"
                                    onClick={() => handleSendReply(conv)}
                                    disabled={isSending || !replyText.trim()}
                                >
                                    {isSending ? 'Enviando...' : 'Enviar'}
                                </button>
                            </div>
                        )}

                        {isConversationOpen && (
                            <div className="scg-close-bar">
                                <button
                                    className="scg-close-btn"
                                    onClick={() => handleCloseConversation(conv.conversation_id)}
                                    disabled={isClosing}
                                >
                                    {isClosing ? 'Encerrando...' : '✖ Encerrar Atendimento'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="scg-page"><div className="scg-loading">Carregando conversas...</div></div>;
    }

    if (error) {
        return <div className="scg-page"><div className="scg-error">{error}</div></div>;
    }

    return (
        <div className="scg-page">
            {confirmCloseId && (
                <div className="scg-modal-overlay" onClick={() => setConfirmCloseId(null)}>
                    <div className="scg-modal" onClick={e => e.stopPropagation()}>
                        <div className="scg-modal-icon">⚠️</div>
                        <h3 className="scg-modal-title">Encerrar Atendimento</h3>
                        <p className="scg-modal-text">
                            Tem certeza que deseja encerrar este atendimento? Esta ação não pode ser desfeita.
                        </p>
                        <div className="scg-modal-actions">
                            <button className="scg-modal-btn scg-modal-cancel" onClick={() => setConfirmCloseId(null)}>
                                Cancelar
                            </button>
                            <button className="scg-modal-btn scg-modal-confirm" onClick={confirmClose}>
                                Sim, Encerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <h1>💬 Suporte Genérico</h1>

            <section className="scg-section">
                <div className="scg-section-header">
                    <h2 className="scg-section-title scg-section-open">
                        🟢 Em Aberto ({openConversations.length})
                    </h2>
                    <button
                        className="scg-refresh-all-btn"
                        onClick={handleRefreshAll}
                        disabled={refreshing}
                        title="Atualizar conversas"
                    >
                        {refreshing ? '⏳ Atualizando...' : '🔄 Atualizar'}
                    </button>
                </div>
                {openConversations.length === 0 ? (
                    <div className="scg-empty">Nenhuma conversa em aberto.</div>
                ) : (
                    openConversations.map(conv => renderConversationCard(conv))
                )}
            </section>

            <section className="scg-section">
                <h2 className="scg-section-title scg-section-closed">
                    🔴 Fechadas ({closedConversations.length})
                </h2>
                {closedConversations.length === 0 ? (
                    <div className="scg-empty">Nenhuma conversa fechada.</div>
                ) : (
                    closedConversations.map(conv => renderConversationCard(conv))
                )}
            </section>
        </div>
    );
};

export default SupportChatGeneric;
